<template>
  <div class="condition-builder">
    <div class="form-group mb-3">
      <label class="fw-bold">Lógica de correspondência</label>
      <select class="custom-select" :value="matchType" @change="$emit('update:matchType', $event.target.value)">
        <option value="all">Todas as condições (AND)</option>
        <option value="any">Qualquer condição (OR)</option>
      </select>
    </div>

    <div class="conditions-list">
      <div v-for="(condition, index) in conditions" :key="index" class="condition-row card card-body mb-2 p-3">
        <div class="row align-items-end">
          <div class="col-md-3">
            <label>Tipo</label>
            <select class="custom-select" v-model="condition.type" @change="onTypeChange(index)">
              <option value="">Selecione...</option>
              <option value="status">Status</option>
              <option value="group">Grupo</option>
              <option value="tag">Tag</option>
              <option value="custom_field">Campo Customizado</option>
              <option value="ticket">Ticket/Ingresso</option>
            </select>
          </div>

          <div class="col-md-2">
            <label>Operador</label>
            <select class="custom-select" v-model="condition.operator">
              <option v-for="op in getOperatorsForType(condition.type)" :key="op.value" :value="op.value">
                {{ op.label }}
              </option>
            </select>
          </div>

          <div class="col-md-3" v-if="condition.type === 'custom_field'">
            <label>Nome do campo</label>
            <input type="text" class="form-control" v-model="condition.field_name" placeholder="Ex: cargo, empresa">
          </div>

          <div class="col-md-3" v-if="!isUnaryOperator(condition.operator)">
            <label>Valor</label>
            <!-- Status dropdown -->
            <select v-if="condition.type === 'status'" class="custom-select" v-model="condition.value" @change="onStatusValueChange(index)">
              <option v-for="s in statuses" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
            <!-- Group dropdown -->
            <select v-else-if="condition.type === 'group'" class="custom-select" v-model="condition.value">
              <option v-for="group in groups" :key="group.id" :value="group.id">{{ group.name }}</option>
            </select>
            <!-- Tag dropdown -->
            <select v-else-if="condition.type === 'tag'" class="custom-select" v-model="condition.value">
              <option v-for="tag in tags" :key="tag.id" :value="tag.name">{{ tag.name }}</option>
            </select>
            <!-- Ticket dropdown -->
            <select v-else-if="condition.type === 'ticket'" class="custom-select" v-model="condition.value">
              <option v-for="ticket in tickets" :key="ticket.id" :value="ticket.id">{{ ticket.name }}</option>
            </select>
            <!-- Default text input -->
            <input v-else type="text" class="form-control" v-model="condition.value" placeholder="Valor">
          </div>

          <!-- Sub-status de pendência (quando status == 2 / Pendente) -->
          <div class="col-md-2" v-if="condition.type === 'status' && isPendente(condition.value) && Object.keys(refStatuses).length > 0">
            <label>Motivo</label>
            <select class="custom-select" v-model="condition.ref_status">
              <option value="">Qualquer</option>
              <option v-for="(name, id) in refStatuses" :key="id" :value="id">{{ name }}</option>
            </select>
          </div>

          <div class="col-md-1 d-flex align-items-end">
            <button type="button" class="btn btn-outline-danger btn-sm" @click="removeCondition(index)" title="Remover condição">
              <i class="bx bx-minus"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <button type="button" class="btn btn-outline-primary btn-sm mt-2" @click="addCondition">
      <i class="bx bx-plus mr-1"></i> Adicionar condição
    </button>
  </div>
</template>

<script>
import http from '@/http'

export default {
  name: 'ConditionBuilder',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    matchType: {
      type: String,
      default: 'all'
    }
  },
  data () {
    return {
      statuses: [],
      refStatuses: {},
      groups: [],
      tags: [],
      tickets: []
    }
  },
  computed: {
    conditions: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  mounted () {
    this.loadOptions()
  },
  methods: {
    async loadOptions () {
      try {
        const [statusRes, refStatusRes, groupsRes, tagsRes] = await Promise.all([
          http.get('/admin/settings/status-select'),
          http.get('/admin/settings/refstatus'),
          http.get('/admin/settings/groups'),
          http.get('/admin/tags')
        ])
        this.statuses = statusRes.data || []
        this.refStatuses = refStatusRes.data || {}
        this.groups = groupsRes.data || []
        this.tags = tagsRes.data.data || tagsRes.data || []

        // Tenta carregar tickets (pode não existir)
        try {
          const ticketsRes = await http.get('/admin/tickets')
          this.tickets = ticketsRes.data.data || ticketsRes.data || []
        } catch (e) {
          this.tickets = []
        }
      } catch (e) {
        console.error('Erro ao carregar opções do ConditionBuilder', e)
      }
    },
    isPendente (value) {
      return String(value) === '2'
    },
    onStatusValueChange (index) {
      const conditions = [...this.conditions]
      // Limpa ref_status quando muda o status
      conditions[index].ref_status = ''
      this.$emit('input', conditions)
    },
    addCondition () {
      const conditions = [...this.conditions]
      conditions.push({ type: '', operator: '==', value: '', field_name: '', ref_status: '' })
      this.$emit('input', conditions)
    },
    removeCondition (index) {
      const conditions = [...this.conditions]
      conditions.splice(index, 1)
      this.$emit('input', conditions)
    },
    onTypeChange (index) {
      const conditions = [...this.conditions]
      conditions[index].operator = '=='
      conditions[index].value = ''
      conditions[index].field_name = ''
      conditions[index].ref_status = ''
      this.$emit('input', conditions)
    },
    isUnaryOperator (operator) {
      return ['is_empty', 'is_not_empty'].includes(operator)
    },
    getOperatorsForType (type) {
      const baseOperators = [
        { value: '==', label: 'Igual a' },
        { value: '!=', label: 'Diferente de' }
      ]

      if (type === 'tag') {
        return [
          { value: 'contains', label: 'Possui' },
          { value: 'not_contains', label: 'Não possui' },
          { value: 'is_empty', label: 'Sem tags' },
          { value: 'is_not_empty', label: 'Com tags' }
        ]
      }

      if (type === 'custom_field') {
        return [
          ...baseOperators,
          { value: 'contains', label: 'Contém' },
          { value: 'not_contains', label: 'Não contém' },
          { value: 'starts_with', label: 'Começa com' },
          { value: 'ends_with', label: 'Termina com' },
          { value: 'is_empty', label: 'Está vazio' },
          { value: 'is_not_empty', label: 'Não está vazio' },
          { value: '>', label: 'Maior que' },
          { value: '<', label: 'Menor que' }
        ]
      }

      if (type === 'ticket') {
        return [
          { value: '==', label: 'Possui ingresso' },
          { value: '!=', label: 'Não possui ingresso' },
          { value: 'is_empty', label: 'Sem ingressos' },
          { value: 'is_not_empty', label: 'Com ingressos' }
        ]
      }

      return baseOperators
    }
  }
}
</script>

<style scoped>
.condition-row {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
}
</style>
